import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import superAdminApi from "../../../lib/superAdminApi";
import SuperAdminLayout from "../../../components/SuperAdminLayout";
import { Search, ChevronLeft, ChevronRight, UserCheck, UserX } from "lucide-react";
import { toast } from "react-toastify";

interface User {
  id: string;
  email: string;
  username: string;
  firstname?: string | null;
  lastname?: string | null;
  is_active: boolean;
  created_at?: string | null;
  role?: string | null;
  Business?: { id: string; name: string } | null;
}

const SuperAdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    superAdminApi
      .get("/superadmin/users", { params: { page, limit: 20, search } })
      .then((res) => {
        setUsers(res.data.users);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      })
      .catch((err) => toast.error(err.response?.data?.message ?? "Failed to load users"))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const toggleActive = async (user: User) => {
    const endpoint = user.is_active
      ? `/superadmin/users/${user.id}/deactivate`
      : `/superadmin/users/${user.id}/activate`;
    try {
      await superAdminApi.put(endpoint);
      toast.success(`User ${user.is_active ? "deactivated" : "activated"}`);
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Action failed");
    }
  };

  return (
    <SuperAdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Users</h1>
            <p className="text-gray-500 text-sm mt-1">{total.toLocaleString()} total users</p>
          </div>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 mb-5">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name, email, username..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 transition-colors">
            Search
          </button>
        </form>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-gray-500">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Business</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Joined</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-36 mb-1.5" />
                        <div className="h-3 bg-gray-200 rounded w-28" />
                      </td>
                      <td className="px-4 py-3"><div className="h-5 bg-gray-200 rounded-full w-16" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                      <td className="px-4 py-3"><div className="h-5 bg-gray-200 rounded-full w-14" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                      <td className="px-4 py-3"><div className="h-7 bg-gray-200 rounded-lg w-7" /></td>
                    </tr>
                  ))
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">No users found</td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/superadmin/users/${user.id}`)}
                          className="text-left hover:text-indigo-600 transition-colors"
                        >
                          <p className="font-medium text-gray-900">
                            {user.firstname} {user.lastname}
                          </p>
                          <p className="text-gray-400 text-xs">{user.email}</p>
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          {user.role ?? "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{user.Business?.name ?? "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                        }`}>
                          {user.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleActive(user)}
                          title={user.is_active ? "Deactivate user" : "Activate user"}
                          className={`p-1.5 rounded-lg transition-colors ${
                            user.is_active
                              ? "text-red-500 hover:bg-red-50"
                              : "text-green-600 hover:bg-green-50"
                          }`}
                        >
                          {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminUsers;
